import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
  Film, Play, Download, Share2, Trash2, X, Loader,
  Search, RefreshCw, Eye, EyeOff, Clock, Calendar,
} from "lucide-react";
import { lmsGetAllRecordings, lmsToggleShareRec, lmsDeleteRecording } from "../api/lmsApi";
import "../lms.css";

const getUser = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}"); } catch { return {}; }
};

const fmtDuration = (ms) => {
  if (!ms) return "—";
  const totalMin = Math.floor(Number(ms) / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const fmtSize = (bytes) => {
  if (!bytes) return "—";
  const mb = bytes / (1024 * 1024);
  return mb > 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
};

/* ── Video Player Modal ── */
function PlayerModal({ rec, onClose }) {
  return createPortal(
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "90%", maxWidth: 900, background: "#000", borderRadius: 12, overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,.6)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", background: "#111" }}>
          <span style={{ color: "#fff", fontWeight: 600, fontSize: 14 }}>{rec.title || rec.class_title || "Recording"}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 13 }}>
            <X size={16} /> Close
          </button>
        </div>
        <video
          src={rec.local_url}
          controls
          autoPlay
          style={{ width: "100%", display: "block", maxHeight: "70vh", background: "#000" }}
        />
        <div style={{ padding: "10px 16px", background: "#111", display: "flex", gap: 16, fontSize: 12, color: "#9ca3af" }}>
          {rec.class_title && <span>Class: {rec.class_title}</span>}
          {rec.batch_name  && <span>Batch: {rec.batch_name}</span>}
          {rec.duration    && <span><Clock size={11} style={{ display: "inline", marginRight: 3 }} />{fmtDuration(rec.duration)}</span>}
          {rec.start_time  && <span><Calendar size={11} style={{ display: "inline", marginRight: 3 }} />{new Date(Number(rec.start_time)).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</span>}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function LmsRecordings() {
  const user         = getUser();
  const schoolId     = user.school_id || user.admin_id;
  const isStudent    = user.role === "student";
  const isTeacher    = user.role === "teacher";
  const isAdmin      = !isStudent && !isTeacher;
  const canManage    = isAdmin || isTeacher;

  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [playing, setPlaying]       = useState(null);
  const [toggling, setToggling]     = useState({});
  const [deleting, setDeleting]     = useState({});
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { school_id: schoolId };
      if (isStudent) params.student_id = user.id || user.student_id;
      const res = await lmsGetAllRecordings(params);
      setRecordings(res.data?.data || []);
    } catch { toast.error("Failed to load recordings"); }
    finally { setLoading(false); }
  }, [schoolId, isStudent]);

  useEffect(() => { load(); }, [load]);

  const filtered = recordings.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.title || "").toLowerCase().includes(q) ||
           (r.class_title || "").toLowerCase().includes(q) ||
           (r.batch_name || "").toLowerCase().includes(q);
  });

  const handleShare = async (rec) => {
    setToggling(t => ({ ...t, [rec.id]: true }));
    try {
      await lmsToggleShareRec(rec.id, { is_shared: rec.is_shared ? 0 : 1, shared_by: user.id });
      toast.success(rec.is_shared ? "Recording unshared" : "Recording shared with students!");
      load();
    } catch { toast.error("Failed to update share status"); }
    finally { setToggling(t => ({ ...t, [rec.id]: false })); }
  };

  const handleDelete = async (rec) => {
    setDeleting(d => ({ ...d, [rec.id]: true }));
    try {
      await lmsDeleteRecording(rec.id);
      toast.success("Recording deleted");
      setConfirmDel(null);
      load();
    } catch { toast.error("Failed to delete recording"); }
    finally { setDeleting(d => ({ ...d, [rec.id]: false })); }
  };

  const handleDownload = (rec) => {
    const a = document.createElement("a");
    a.href = rec.local_url;
    a.download = `${rec.title || "recording"}.mp4`;
    a.click();
  };

  return (
    <div className="lms-content-inner">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontWeight: 700, fontSize: 20, margin: 0, color: "#111827", display: "flex", alignItems: "center", gap: 8 }}>
            <Film size={20} color="#4f46e5" /> Class Recordings
          </h2>
          <p style={{ margin: "3px 0 0", color: "#6b7280", fontSize: 13 }}>
            {isStudent ? "Recordings shared by your teachers" : "All recorded live classes"}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* Search */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, border: "1px solid #e5e7eb", borderRadius: 9, padding: "0 12px", height: 38, background: "#fff", minWidth: 220 }}>
            <Search size={14} color="#9ca3af" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search recordings..."
              style={{ border: 0, outline: "none", fontSize: 13, color: "#111827", background: "none", width: "100%" }} />
          </div>
          <button onClick={load} style={{ display: "flex", alignItems: "center", gap: 5, height: 38, padding: "0 14px", borderRadius: 9, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats bar */}
      {!isStudent && (
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          {[
            { label: "Total",   val: recordings.length,                            color: "#4f46e5", bg: "#e0e7ff" },
            { label: "Shared",  val: recordings.filter(r => r.is_shared).length,   color: "#16a34a", bg: "#dcfce7" },
            { label: "Private", val: recordings.filter(r => !r.is_shared).length,  color: "#f59e0b", bg: "#fef3c7" },
          ].map(s => (
            <div key={s.label} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 20px", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 38, height: 38, borderRadius: 9, background: s.bg, color: s.color, display: "grid", placeItems: "center", fontWeight: 800, fontSize: 18 }}>{s.val}</div>
              <span style={{ fontSize: 13, color: "#6b7280", fontWeight: 600 }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {/* Table head */}
        <div style={{ display: "grid", gridTemplateColumns: canManage ? "2fr 1.5fr 1fr 1fr 1fr auto" : "2fr 1.5fr 1fr 1fr auto", padding: "10px 18px", background: "#f8fafc", borderBottom: "1px solid #e5e7eb", fontSize: 11, fontWeight: 700, color: "#6b7280", letterSpacing: ".05em" }}>
          <span>RECORDING</span>
          <span>CLASS / BATCH</span>
          <span>DATE</span>
          <span>DURATION</span>
          {canManage && <span>STATUS</span>}
          <span>ACTIONS</span>
        </div>

        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <Loader size={28} className="ms2-spin" color="#4f46e5" />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <Film size={44} color="#e5e7eb" style={{ margin: "0 auto 12px", display: "block" }} />
            <div style={{ fontWeight: 600, color: "#6b7280", fontSize: 15 }}>
              {isStudent ? "No recordings shared with you yet" : "No recordings found"}
            </div>
            <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 4 }}>
              {isStudent ? "Ask your teacher to share a recording" : "Recordings appear here after you save them from a live class"}
            </div>
          </div>
        ) : (
          filtered.map((rec, i) => (
            <div key={rec.id} style={{ display: "grid", gridTemplateColumns: canManage ? "2fr 1.5fr 1fr 1fr 1fr auto" : "2fr 1.5fr 1fr 1fr auto", padding: "14px 18px", borderBottom: "1px solid #f1f5f9", alignItems: "center", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
              {/* Title */}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 38, height: 38, borderRadius: 9, background: "#e0e7ff", color: "#4f46e5", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <Film size={17} />
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>{rec.title || `Recording ${rec.id}`}</div>
                  {rec.file_size > 0 && <div style={{ fontSize: 11, color: "#9ca3af" }}>{fmtSize(rec.file_size)}</div>}
                </div>
              </div>

              {/* Class / Batch */}
              <div>
                <div style={{ fontSize: 13, color: "#374151", fontWeight: 500 }}>{rec.class_title || "—"}</div>
                {rec.batch_name && <div style={{ fontSize: 11, color: "#9ca3af" }}>{rec.batch_name}</div>}
              </div>

              {/* Date */}
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {rec.start_time ? new Date(Number(rec.start_time)).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
              </div>

              {/* Duration */}
              <div style={{ fontSize: 13, color: "#374151", fontWeight: 600 }}>{fmtDuration(rec.duration)}</div>

              {/* Share status (admin/teacher only) */}
              {canManage && (
                <div>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, background: rec.is_shared ? "#dcfce7" : "#f3f4f6", color: rec.is_shared ? "#16a34a" : "#6b7280" }}>
                    {rec.is_shared ? "Shared" : "Private"}
                  </span>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {/* Watch */}
                {rec.local_url && (
                  <button onClick={() => setPlaying(rec)} title="Watch"
                    style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 12px", borderRadius: 7, background: "#4f46e5", color: "#fff", border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    <Play size={12} /> Watch
                  </button>
                )}

                {/* Download (admin/teacher only) */}
                {canManage && rec.local_url && (
                  <button onClick={() => handleDownload(rec)} title="Download MP4"
                    style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 7, background: "#f0fdf4", color: "#16a34a", border: "1px solid #86efac", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    <Download size={12} />
                  </button>
                )}

                {/* Share toggle (admin/teacher only) */}
                {canManage && (
                  <button onClick={() => handleShare(rec)} disabled={toggling[rec.id]} title={rec.is_shared ? "Unshare from students" : "Share with students"}
                    style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 7, background: rec.is_shared ? "#fef3c7" : "#e0e7ff", color: rec.is_shared ? "#92400e" : "#4f46e5", border: `1px solid ${rec.is_shared ? "#fde68a" : "#c7d2fe"}`, fontSize: 12, fontWeight: 600, cursor: "pointer", opacity: toggling[rec.id] ? 0.6 : 1 }}>
                    {toggling[rec.id] ? <Loader size={11} className="ms2-spin" /> : rec.is_shared ? <EyeOff size={12} /> : <Share2 size={12} />}
                  </button>
                )}

                {/* Delete (admin only) */}
                {isAdmin && (
                  <button onClick={() => setConfirmDel(rec)} title="Delete"
                    style={{ display: "flex", alignItems: "center", gap: 4, padding: "6px 10px", borderRadius: 7, background: "#fff5f5", color: "#dc2626", border: "1px solid #fca5a5", fontSize: 12, cursor: "pointer" }}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Video Player Modal */}
      {playing && <PlayerModal rec={playing} onClose={() => setPlaying(null)} />}

      {/* Delete Confirm */}
      {confirmDel && createPortal(
        <div onClick={() => setConfirmDel(null)} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, padding: 28, maxWidth: 380, width: "90%", textAlign: "center" }}>
            <Trash2 size={36} color="#dc2626" style={{ margin: "0 auto 12px", display: "block" }} />
            <div style={{ fontWeight: 700, fontSize: 16, color: "#111827", marginBottom: 6 }}>Delete Recording?</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 20 }}>
              "<b>{confirmDel.title || "this recording"}</b>" will be permanently deleted from DocMaster.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={() => setConfirmDel(null)} style={{ padding: "9px 20px", borderRadius: 9, border: "1px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancel</button>
              <button onClick={() => handleDelete(confirmDel)} disabled={deleting[confirmDel.id]}
                style={{ padding: "9px 20px", borderRadius: 9, border: "none", background: "#dc2626", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                {deleting[confirmDel.id] ? <Loader size={13} className="ms2-spin" /> : null} Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
